// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { invitationApi } from '../api/invitationApi';
import { profileRequestApi } from '../api/profileRequestApi';
import { policyApi } from '../api/policyApi';
import { Alert, useToast } from '@loa/ui-components';
import { EmailComposerPanel } from '../components/email/EmailComposerPanel';
import { RecipientInfoCard } from '../components/email/RecipientInfoCard';
import { PoliciesCard } from '../components/email/PoliciesCard';
import { OutstandingRequestsTable } from '../components/requests/OutstandingRequestsTable';
import { deriveDisplayStatus } from '../components/requests/RequestDisplayStatusBadge';
import { clientIdForRequest } from '../components/requests/requestCells';
import type { ProfileRequestDTO } from '../types/profileRequest';
import type { Policy } from '../types/policy';
import type {
  ResendClientInfo,
  ResendRateLimitWarning,
  EmailTemplate,
} from '../types/invitation';

interface PageState {
  loading: boolean;
  error: string | null;
  clientInfo: ResendClientInfo | null;
  defaultTemplate: EmailTemplate | null;
  rateLimitWarning: ResendRateLimitWarning | null;
}

interface EmailState {
  subject: string;
  body: string;
  isModified: boolean;
}

interface SendResult {
  message: string;
  success: boolean;
}

interface DispatchArgs {
  profileId: string;
  clientId: string;
  isSendMode: boolean;
  emailState: EmailState;
}

interface ApiResult {
  success: boolean;
  message?: string;
}

async function dispatchInvitation(args: DispatchArgs): Promise<ApiResult> {
  const { profileId, clientId, isSendMode, emailState } = args;
  if (isSendMode) {
    const request = emailState.isModified
      ? { customSubject: emailState.subject, customBody: emailState.body }
      : {};
    return invitationApi.sendInvitationWithPreview(profileId, clientId, request);
  }
  const customization = emailState.isModified
    ? {
        customSubject: emailState.subject,
        customBody: emailState.body,
        forceResend: true,
      }
    : { forceResend: true };
  return invitationApi.resendInvitationWithCustomization(profileId, clientId, customization);
}

function deriveSendErrorMessage(err: unknown): string {
  if (!navigator.onLine) {
    return 'No internet connection. Please check your network.';
  }
  const error = err as { response?: { status?: number; data?: { message?: string; details?: string } } };
  if (error.response?.status === 422) {
    return 'Email cannot be sent. SMTP is not configured for this brokerage.';
  }
  if (error.response?.status === 429) {
    return 'Rate limit exceeded. Please wait before sending again.';
  }
  if (error.response?.data?.details) {
    return error.response.data.details;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Failed to send invitation';
}

const MAX_SUBJECT_LENGTH = 200;

export function ResendInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const profileId = searchParams.get('profileId');
  const clientId = searchParams.get('clientId');
  const mode = searchParams.get('mode') || 'resend';
  // Trailing client ids to nudge after this one (primary→secondary sequence).
  const nextClients = (searchParams.get('nextClients') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const { showToast } = useToast();
  const isSendMode = mode === 'send';

  // Page state
  const [pageState, setPageState] = useState<PageState>({
    loading: true,
    error: null,
    clientInfo: null,
    defaultTemplate: null,
    rateLimitWarning: null,
  });

  // Outstanding requests for this client — drives the table shown above the
  // composer (replaces the legacy "policies requiring signature" table).
  const [outstandingRequests, setOutstandingRequests] = useState<ProfileRequestDTO[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);

  // Email editor state
  const [emailState, setEmailState] = useState<EmailState>({
    subject: '',
    body: '',
    isModified: false,
  });

  // Sending state
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  // Fetch client data and template
  const fetchData = useCallback(async () => {
    if (!profileId || !clientId) {
      setPageState({
        loading: false,
        error: !profileId ? 'Profile ID is required' : 'Client ID is required',
        clientInfo: null,
        defaultTemplate: null,
        rateLimitWarning: null,
      });
      return;
    }

    try {
      setPageState((prev) => ({ ...prev, loading: true, error: null }));

      const data = await invitationApi.getResendInvitationData(profileId, clientId);

      setPageState({
        loading: false,
        error: null,
        clientInfo: data.clientInfo,
        defaultTemplate: data.defaultTemplate,
        rateLimitWarning: data.rateLimitWarning,
      });

      setEmailState({
        subject: data.defaultTemplate.subject,
        body: data.defaultTemplate.editableBodyHtml,
        isModified: false,
      });
    } catch (err) {
      console.error('Failed to fetch invitation data:', err);
      const error = err as { response?: { status?: number; data?: { message?: string } } };

      let errorMessage = 'Failed to load invitation data';
      if (error.response?.status === 404) {
        errorMessage = 'Client or profile not found';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have access to this profile';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setPageState({
        loading: false,
        error: errorMessage,
        clientInfo: null,
        defaultTemplate: null,
        rateLimitWarning: null,
      });
    }
  }, [profileId, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Best-effort load of this profile's requests + policies for the
  // outstanding-requests table. Kept separate from fetchData so a slow/failed
  // fetch never blocks the composer (mirrors NudgeProviderPage).
  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    (async () => {
      try {
        const [requestPage, profilePolicies] = await Promise.all([
          profileRequestApi.list(profileId),
          policyApi.getPoliciesByProfile(profileId),
        ]);
        if (cancelled) return;
        setOutstandingRequests(requestPage.content);
        setPolicies(profilePolicies);
      } catch {
        if (cancelled) return;
        setOutstandingRequests([]);
        setPolicies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  // Email handlers
  const handleSubjectChange = useCallback((newSubject: string) => {
    if (newSubject.length <= MAX_SUBJECT_LENGTH) {
      setEmailState((prev) => ({
        ...prev,
        subject: newSubject,
        isModified: true,
      }));
    }
  }, []);

  const handleBodyChange = useCallback((body: string) => {
    setEmailState((prev) => ({
      ...prev,
      body,
      isModified: true,
    }));
  }, []);

  const handleReset = useCallback(() => {
    if (pageState.defaultTemplate) {
      setEmailState({
        subject: pageState.defaultTemplate.subject,
        body: pageState.defaultTemplate.editableBodyHtml,
        isModified: false,
      });
    }
  }, [pageState.defaultTemplate]);

  const handleCancel = useCallback(() => {
    navigate(`/relationships/${profileId}`);
  }, [navigate, profileId]);

  // Send handler - works for both send and resend
  const handleSend = useCallback(async () => {
    if (!profileId || !clientId) return;

    setSending(true);
    setSendResult(null);

    const toastRecipient = pageState.clientInfo?.clientName || pageState.clientInfo?.clientEmail;

    try {
      const result = await dispatchInvitation({
        profileId,
        clientId,
        isSendMode,
        emailState,
      });

      if (result.success) {
        showToast(`Digital Letter of Authority sent. Consent sent to ${toastRecipient}`);
        // Multi-client nudge: advance to the next queued client instead of
        // returning to the relationship. Empty queue → back to the relationship.
        if (nextClients.length > 0) {
          const [next, ...rest] = nextClients;
          const queue = rest.length ? `&nextClients=${rest.join(',')}` : '';
          navigate(`/resend-invitation?profileId=${profileId}&clientId=${next}&mode=resend${queue}`);
          return;
        }
        navigate(`/relationships/${profileId}`);
        return;
      }
      setSendResult({
        message: result.message || 'Failed to send invitation',
        success: false,
      });
    } catch (err) {
      console.error('Failed to send invitation:', err);
      setSendResult({
        message: deriveSendErrorMessage(err),
        success: false,
      });
    } finally {
      setSending(false);
    }
  }, [profileId, clientId, emailState, pageState.clientInfo, navigate, isSendMode, showToast, nextClients]);

  const policyById = useMemo(() => {
    const map = new Map<string, Policy>();
    for (const p of policies) map.set(p.id, p);
    return map;
  }, [policies]);

  // This client's outstanding (pending-client) requests — shown above the
  // composer so the advisor sees exactly what the nudge covers.
  const clientRequests = useMemo(
    () =>
      outstandingRequests.filter((r) => {
        if (deriveDisplayStatus(r.status, r.actions) !== 'PENDING_CLIENT') return false;
        const policy = r.policyId ? policyById.get(r.policyId) : null;
        return clientIdForRequest(r, policy) === clientId;
      }),
    [outstandingRequests, policyById, clientId],
  );

  // Loading state
  if (pageState.loading) {
    return (
      <div className="w-full py-8 px-6" data-testid="resend-invitation-page">
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-purple-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-grey-02" data-testid="loading-indicator">Loading invitation data...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState.error || !pageState.clientInfo || !pageState.defaultTemplate) {
    return (
      <div className="w-full py-8 px-6" data-testid="resend-invitation-page">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
          <p className="text-sm font-semibold text-red-700 mb-1">Error Loading Page</p>
          <p className="text-sm text-red-700">{pageState.error || 'Failed to load invitation data'}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-2 text-sm text-purple-primary hover:underline"
            data-testid="error-go-back"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { clientInfo, rateLimitWarning } = pageState;

  return (
    <EmailComposerPanel
      subject={emailState.subject}
      onSubjectChange={handleSubjectChange}
      recipients={clientInfo.clientEmail}
      body={emailState.body}
      onBodyChange={handleBodyChange}
      isModified={emailState.isModified}
      onReset={handleReset}
      onCancel={handleCancel}
      onSend={handleSend}
      sendLabel="Send Email"
      sending={sending}
      error={sendResult && !sendResult.success ? sendResult.message : null}
    >
      <RecipientInfoCard
        title={clientInfo.clientName}
        email={clientInfo.clientEmail}
      />

      {clientRequests.length > 0 && (
        <PoliciesCard
          title={`Outstanding requests (${clientRequests.length})`}
          description="These are the outstanding requests this client needs to action."
        >
          <OutstandingRequestsTable
            requests={clientRequests}
            policyById={policyById}
            testId="outstanding-requests-table"
          />
        </PoliciesCard>
      )}

      {/* Rate Limit Warning */}
      {rateLimitWarning && (
        <div className="mx-lg mt-md">
          <Alert variant="warning" header="Rate Limit Warning" data-testid="rate-limit-warning">
            <p>{rateLimitWarning.message}</p>
          </Alert>
        </div>
      )}
    </EmailComposerPanel>
  );
}

export default ResendInvitationPage;
